import { useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Download, FileDown } from "lucide-react";
import { toast } from "sonner";

export default function ImportPage() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [downloadingTpl, setDownloadingTpl] = useState(false);

  const downloadTemplate = async () => {
    setDownloadingTpl(true);
    try {
      const res = await api.get("/imports/voters/template", { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "Template_Impor_DPS.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      toast.success("Template diunduh");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDownloadingTpl(false); }
  };

  const doPreview = async () => {
    if (!file) { toast.error("Pilih file dulu"); return; }
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/imports/voters/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); setPreview(null); }
    finally { setLoading(false); }
  };

  const doCommit = async () => {
    if (!file) return;
    if (!window.confirm("Yakin ingin mengimpor data ini? Duplikat akan dilewati.")) return;
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/imports/voters/commit?skip_duplicates=true", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setPreview(null);
      setFile(null);
      toast.success(`${data.inserted} pemilih diimpor.`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCommitting(false); }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="import-page">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Impor DPS</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Impor Data Pemilih</h1>
        <p className="text-slate-600 text-sm mt-2 max-w-2xl">
          Unggah file Excel (.xlsx) atau CSV. Kolom yang dikenali: <code className="font-mono">nik, nama, jenis_kelamin, tempat_lahir, tanggal_lahir, status_perkawinan, alamat, rt, rw, dusun, tps, disabilitas, keterangan</code>.
          NIK akan otomatis diperiksa untuk hindari duplikat.
        </p>
      </header>

      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-emerald-700 text-white grid place-items-center flex-shrink-0">
            <FileSpreadsheet className="h-5 w-5" />
          </div>
          <div>
            <div className="font-semibold text-emerald-900">Belum punya format?</div>
            <div className="text-sm text-emerald-800">Unduh template Excel yang sudah disiapkan dengan validasi kolom + petunjuk pengisian.</div>
          </div>
        </div>
        <Button
          onClick={downloadTemplate}
          disabled={downloadingTpl}
          className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold"
          data-testid="download-template-button"
        >
          <FileDown className="h-4 w-4 mr-2" /> {downloadingTpl ? "Mengunduh..." : "Unduh Template Excel"}
        </Button>
      </div>

      <div className="rounded-md border-2 border-dashed border-slate-300 bg-white p-8 text-center">
        <FileSpreadsheet className="h-10 w-10 mx-auto text-slate-400 mb-3" />
        <label className="inline-flex items-center gap-2 cursor-pointer bg-slate-900 hover:bg-slate-800 text-white font-semibold px-5 py-2.5 rounded-md">
          <Upload className="h-4 w-4" /> Pilih File
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null); }}
            data-testid="import-file-input"
          />
        </label>
        {file && (
          <div className="mt-4 text-sm text-slate-700">
            <span className="font-semibold">{file.name}</span> · {(file.size / 1024).toFixed(1)} KB
          </div>
        )}
        {file && !preview && (
          <Button onClick={doPreview} disabled={loading} className="mt-4 bg-emerald-700 hover:bg-emerald-800" data-testid="preview-button">
            {loading ? "Memproses..." : "Pratinjau"}
          </Button>
        )}
      </div>

      {preview && (
        <section className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <SummaryTile label="Total Baris Valid" value={preview.total_rows} tone="primary" />
            <SummaryTile label="Duplikat (di-skip)" value={preview.total_duplicates} tone="warning" />
            <SummaryTile label="Error" value={preview.total_errors} tone="danger" />
            <SummaryTile label="Akan Diimpor" value={preview.total_rows - preview.total_duplicates} tone="success" />
          </div>

          {preview.errors?.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-center gap-2 text-amber-900 font-semibold mb-2"><AlertTriangle className="h-4 w-4" /> Baris bermasalah</div>
              <ul className="text-xs text-amber-900 space-y-0.5 max-h-32 overflow-y-auto">
                {preview.errors.map((e, i) => <li key={i}>Baris {e.row}: {e.error}</li>)}
              </ul>
            </div>
          )}

          <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">NIK</th>
                  <th className="px-3 py-2 text-left">Nama</th>
                  <th className="px-3 py-2 text-left">JK</th>
                  <th className="px-3 py-2 text-left">Alamat</th>
                  <th className="px-3 py-2 text-left">RT/RW</th>
                  <th className="px-3 py-2 text-left">Dusun</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.slice(0, 50).map((r, i) => (
                  <tr key={i} className={`border-t border-slate-100 ${r._duplicate ? "bg-amber-50" : ""}`}>
                    <td className="px-3 py-1.5 font-mono">{r.nik}</td>
                    <td className="px-3 py-1.5 font-semibold">{r.nama}</td>
                    <td className="px-3 py-1.5">{r.jenis_kelamin}</td>
                    <td className="px-3 py-1.5 text-slate-600 truncate max-w-xs">{r.alamat || "-"}</td>
                    <td className="px-3 py-1.5 font-mono">{r.rt || "-"}/{r.rw || "-"}</td>
                    <td className="px-3 py-1.5">{r.dusun || "-"}</td>
                    <td className="px-3 py-1.5">
                      {r._duplicate
                        ? <span className="text-amber-700 font-semibold">Duplikat</span>
                        : <span className="text-emerald-700 font-semibold">Baru</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.rows.length > 50 && (
              <div className="px-3 py-2 text-xs text-slate-500 bg-slate-50 border-t border-slate-200">
                Menampilkan 50 dari {preview.rows.length} baris pratinjau.
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => { setPreview(null); setFile(null); }}>Batal</Button>
            <Button onClick={doCommit} disabled={committing} className="bg-slate-900 hover:bg-slate-800" data-testid="commit-button">
              {committing ? "Mengimpor..." : `Impor ${preview.total_rows - preview.total_duplicates} Pemilih`}
            </Button>
          </div>
        </section>
      )}

      {result && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-6">
          <div className="flex items-center gap-2 text-emerald-900 font-display font-bold text-xl"><CheckCircle2 className="h-5 w-5" /> Berhasil diimpor</div>
          <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
            <Info label="Dimasukkan" value={result.inserted} />
            <Info label="Dilewati (duplikat)" value={result.skipped} />
            <Info label="Error" value={result.total_errors} />
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, value, tone = "primary" }) {
  const cls = {
    primary: "bg-slate-900 text-white",
    success: "bg-emerald-50 border border-emerald-200 text-emerald-900",
    warning: "bg-amber-50 border border-amber-200 text-amber-900",
    danger: "bg-red-50 border border-red-200 text-red-900",
  }[tone];
  return (
    <div className={`rounded-md p-4 ${cls}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold opacity-80">{label}</div>
      <div className="font-display text-3xl font-bold tracking-tight mt-1">{value}</div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-800 font-semibold">{label}</div>
      <div className="font-display text-2xl font-bold">{value}</div>
    </div>
  );
}
